import { Heart } from 'lucide-react'
import { useNavigate } from 'react-router'
import { LotImageSlider } from 'shared/components/LotImageSlider'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'
import { getLotAvailability, getVariantChips, type Inventory } from '../lib/availability'

export interface FeedLot {
	_id: string
	title: string
	photoUrls: string[]
	description?: string
	priceUzs: number
	categoryIds: string[]
	isBookmarked: boolean
	inventory: Inventory
}

/** Editorial card (design 2a): photo slider + urgency pill + heart, then title/price/size chips. */
export function LotCard({
	lot,
	categoryNames,
	onToggleBookmark,
}: {
	lot: FeedLot
	categoryNames: string[]
	onToggleBookmark: (lotId: string) => void
}) {
	const { t } = useTranslate()
	const navigate = useNavigate()
	const availability = getLotAvailability(lot.inventory)
	const variants = getVariantChips(lot.inventory)
	const soldOut = availability.kind === 'soldout'
	const textColor = soldOut
		? 'text-[var(--tgui--hint_color,rgb(170,170,170))]'
		: 'text-[var(--tgui--text_color,#fff)]'

	return (
		<div
			role='button'
			tabIndex={0}
			onClick={() => navigate(`/lot/${lot._id}`)}
			onKeyDown={(e) => {
				if (e.key === 'Enter') navigate(`/lot/${lot._id}`)
			}}
			className='cursor-pointer overflow-hidden rounded-[18px] bg-[var(--tgui--card_bg_color,rgb(50,50,50))] shadow-[0_3px_10px_rgba(0,0,0,.34),inset_0_0_0_1px_rgba(255,255,255,.1)]'
		>
			<div className='relative'>
				<LotImageSlider
					photoUrls={lot.photoUrls}
					alt={lot.title}
					imgClassName={
						soldOut
							? 'aspect-[4/5] brightness-[.6] grayscale'
							: 'aspect-[4/5]'
					}
				/>
				{/* {availability.kind === 'low' && (
					<span className="absolute left-3 top-3 rounded-full bg-[rgb(255,159,10)] px-[11px] py-1.5 text-xs font-bold text-[#1a1200]">
						Осталось {availability.left}
					</span>
				)}
				{availability.kind === 'available' && (
					<span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/50 px-[11px] py-1.5 text-xs font-semibold text-white">
						<span className="size-[7px] rounded-full bg-[rgb(50,229,94)]" />
						В наличии
					</span>
				)}
				{soldOut && (
					<span className="absolute left-3 top-3 rounded-full bg-black/70 px-[11px] py-1.5 text-xs font-bold text-white">
						Раскуплено
					</span>
				)} */}
				{!soldOut && (
					<button
						type='button'
						aria-label={
							lot.isBookmarked
								? t('pages.feed.removeBookmark')
								: t('pages.feed.addBookmark')
						}
						onClick={(e) => {
							e.stopPropagation()
							onToggleBookmark(lot._id)
						}}
						className='absolute right-3 top-[11px] grid size-[34px] place-items-center rounded-full bg-black/40 text-white'
					>
						<Heart
							className={
								lot.isBookmarked
									? 'size-[19px] fill-[var(--tgui--button_color,rgb(41,144,255))] text-[var(--tgui--button_color,rgb(41,144,255))]'
									: 'size-[19px]'
							}
						/>
					</button>
				)}
			</div>
			<div className='px-4 pb-4 pt-3.5'>
				<div className='flex items-start justify-between gap-3'>
					<div className='min-w-0'>
						<div className={`truncate text-[17px] font-semibold ${textColor}`}>
							{lot.title}
						</div>
						{categoryNames.length > 0 && (
							<div className='mt-0.5 text-[13px] text-[var(--tgui--hint_color,rgb(170,170,170))]'>
								{categoryNames.join(' · ')}
							</div>
						)}
						{lot.description && (
							<div className='mt-2 whitespace-pre-wrap text-sm text-[var(--tgui--hint_color,rgb(170,170,170))]'>
								{lot.description}
							</div>
						)}
					</div>
					<div
						className={`whitespace-nowrap text-right text-xl font-bold ${textColor}`}
					>
						{formatUzsAmount(lot.priceUzs)}
						<span className='block text-xs font-normal text-[var(--tgui--hint_color,rgb(170,170,170))]'>
							{t('common.currency')}
						</span>
					</div>
				</div>
				<div className='mt-3.5 flex flex-wrap gap-[7px]'>
					{variants.map(({ name, available }) => (
						<span
							key={name}
							className={
								available
									? 'inline-flex h-8 items-center rounded-[9px] px-3.5 text-sm font-semibold text-[var(--tgui--text_color,#fff)] shadow-[inset_0_0_0_1.5px_var(--tgui--button_color,rgb(41,144,255))]'
									: 'inline-flex h-8 items-center rounded-[9px] bg-[rgba(255,255,255,0.08)] px-3.5 text-sm font-semibold text-[var(--tgui--hint_color,rgb(170,170,170))] line-through'
							}
						>
							{name}
						</span>
					))}
				</div>
			</div>
		</div>
	)
}
